/**
 * React Hook for LiveKit Voice Agent Integration
 *
 * This hook provides integration with LiveKit voice agents using:
 * - STT: Groq whisper-large-v3-turbo (backend)
 * - LLM: Kimi K2 Instruct via LiveKit Inference (backend)
 * - TTS: Groq orpheus-tts (backend)
 *
 * Features:
 * - WebRTC-based low-latency audio streaming
 * - Automatic audio track management
 * - Agent state tracking (initializing, listening, thinking, speaking)
 * - RPC method support for bidirectional communication
 * - Participant event handling
 *
 * Token Flow:
 * 1. Client calls tokenEndpoint (your Flask API) with workspace/chat/agent IDs
 * 2. Backend validates workspace and generates JWT with metadata
 * 3. Client connects to LiveKit Cloud with the token
 * 4. LiveKit Cloud auto-dispatches an agent to the room
 * 5. Agent reads metadata from participant to load correct config/history
 *
 * Usage:
 * const {
 *   isConnected,
 *   agentState,
 *   connect,
 *   disconnect,
 *   setMicrophoneEnabled,
 * } = useLiveKitVoice({
 *   tokenEndpoint: 'https://aiv.toothfairyai.com/token',
 *   workspaceId: 'workspace-123',
 *   chatId: 'chat-456',
 *   agentId: 'agent-789',
 *   apiKey: 'your-api-key',
 * });
 */

import { useState, useEffect, useRef, useCallback } from "react";
import envConfig from "../../envConfig";

// Check if running in Electron
const isElectron = () => {
  return typeof window !== "undefined" && window.electronAPI !== undefined;
};

// LiveKit agent states (synced with backend AgentSession)
const AGENT_STATES = {
  INITIALIZING: "initializing",
  LISTENING: "listening",
  THINKING: "thinking",
  SPEAKING: "speaking",
  DISCONNECTED: "disconnected",
};

// Connection states
const CONNECTION_STATES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

const useLiveKitVoice = (options) => {
  const {
    serverUrl,
    tokenEndpoint,
    // Live-session mode: instead of hitting the raw token endpoint, call
    // the /live service (tokenFn) so the backend tracks the voice segment
    // and enforces the per-chat mode lock. tokenFn must resolve to
    // {token, room_name, url}. stopFn (optional) is called on disconnect
    // to release the mode lock.
    tokenFn,
    stopFn,
    workspaceId,
    apiKey,
    chatId,
    agentId,
    roomName: customRoomName,
    participantName = "user",
    onAgentStateChange,
    onTranscript,
    onError,
    autoConnect = false,
  } = options;

  // State
  const [state, setState] = useState({
    connectionState: CONNECTION_STATES.DISCONNECTED,
    agentState: AGENT_STATES.DISCONNECTED,
    isMicrophoneEnabled: false,
    isAgentSpeaking: false,
    transcript: "",
    agentTranscript: "",
    error: null,
    roomName: null,
    audioLevel: 0, // 0-1 scale for audio input level
    isAudioDetected: false, // True when audio input is above threshold
    audioSpectrum: [], // 24 equalizer bands (0-1) for the voice wave UI
  });

  // Refs for LiveKit SDK (dynamically loaded)
  const roomRef = useRef(null);
  const liveKitModuleRef = useRef(null);
  const audioElementsRef = useRef([]);
  const reconnectAttemptsRef = useRef(0);
  const audioLevelIntervalRef = useRef(null);
  const selectedAudioOutputDeviceRef = useRef(null); // Store selected output device ID
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);

  /**
   * Check and request microphone permission (especially for Electron)
   * Call this before enabling microphone to ensure permissions are granted
   */
  const checkMicrophonePermission = useCallback(async () => {
    console.log("[LiveKit] Checking microphone permission...");

    // Check Electron permissions first
    if (isElectron()) {
      try {
        const status = await window.electronAPI.getMicrophonePermissionStatus();
        console.log("[LiveKit] Electron microphone status:", status);
        return {
          granted: status === "granted",
          status,
          isElectron: true,
        };
      } catch (err) {
        console.error("[LiveKit] Failed to check Electron permission:", err);
      }
    }

    // Fallback to standard web API
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: "microphone" });
        return {
          granted: result.state === "granted",
          status: result.state,
          isElectron: false,
        };
      }
    } catch (err) {
      console.log("[LiveKit] Permissions API not available:", err.message);
    }

    // Try to enumerate devices as a permission check
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasLabels = devices.some(d => d.kind === "audioinput" && d.label);
      return {
        granted: hasLabels,
        status: hasLabels ? "granted" : "prompt",
        isElectron: false,
      };
    } catch (err) {
      return {
        granted: false,
        status: "error",
        error: err.message,
        isElectron: false,
      };
    }
  }, []);

  /**
   * Request microphone permission (especially for Electron)
   */
  const requestMicrophonePermission = useCallback(async () => {
    console.log("[LiveKit] Requesting microphone permission...");

    // Request Electron permissions first
    if (isElectron()) {
      try {
        const result = await window.electronAPI.requestMicrophonePermission();
        console.log("[LiveKit] Electron permission request result:", result);

        if (!result.granted && (result.status === "denied" || result.status === "restricted")) {
          // Open system preferences to let user enable
          await window.electronAPI.openSystemPreferencesMicrophone();
        }

        return result;
      } catch (err) {
        console.error("[LiveKit] Failed to request Electron permission:", err);
      }
    }

    // Fallback to standard getUserMedia to trigger permission prompt
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed to trigger the permission
      stream.getTracks().forEach(track => track.stop());
      return { granted: true, status: "granted" };
    } catch (err) {
      console.error("[LiveKit] getUserMedia failed:", err);
      return {
        granted: false,
        status: err.name === "NotAllowedError" ? "denied" : "error",
        error: err.message,
      };
    }
  }, []);

  /**
   * Dynamically load LiveKit SDK
   * This allows the app to work even if livekit-client isn't installed
   */
  const loadLiveKitSDK = useCallback(async () => {
    if (liveKitModuleRef.current) {
      return liveKitModuleRef.current;
    }

    try {
      // Dynamic import of livekit-client
      const livekit = await import("livekit-client");
      liveKitModuleRef.current = livekit;
      console.log("[LiveKit] SDK loaded successfully");
      return livekit;
    } catch (error) {
      console.error("[LiveKit] Failed to load SDK:", error);
      console.warn(
        "[LiveKit] Please install livekit-client: npm install livekit-client"
      );
      throw new Error(
        "LiveKit SDK not available. Install with: npm install livekit-client"
      );
    }
  }, []);

  /**
   * Start monitoring audio level from microphone
   * @param {string} [deviceId] - Optional input device to monitor (falls back to system default)
   */
  const startAudioLevelMonitoring = useCallback(async (deviceId = null) => {
    try {
      console.log("[LiveKit] Starting audio level monitoring...");

      // Get microphone stream — honor the user-selected device (same
      // constraint pattern as AudioRecorder: exact deviceId)
      console.log("[LiveKit] Requesting microphone access for audio monitoring...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      });
      micStreamRef.current = stream;

      // Log stream details
      const audioTracks = stream.getAudioTracks();
      console.log("[LiveKit] Microphone stream acquired:", {
        trackCount: audioTracks.length,
        active: stream.active,
      });

      audioTracks.forEach((track, i) => {
        const settings = track.getSettings();
        console.log(`[LiveKit] Audio track ${i + 1}:`, {
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          deviceId: settings.deviceId?.slice(0, 8) + '...',
          channelCount: settings.channelCount,
          sampleRate: settings.sampleRate,
          sampleSize: settings.sampleSize,
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
          autoGainControl: settings.autoGainControl,
        });
      });

      // Create audio context and analyser
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      // Mobile browsers (notably iOS Safari) create the AudioContext in a
      // suspended state; we're inside the Start-button gesture chain, so
      // resume it now or the analyser never sees samples and the voice
      // wave/audio detection stays dead.
      if (audioContext.state === "suspended") {
        try {
          await audioContext.resume();
          console.log("[LiveKit] AudioContext resumed (was suspended)");
        } catch (err) {
          console.warn("[LiveKit] AudioContext resume failed:", err);
        }
      }
      console.log("[LiveKit] AudioContext created:", {
        state: audioContext.state,
        sampleRate: audioContext.sampleRate,
        baseLatency: audioContext.baseLatency,
      });

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;
      console.log("[LiveKit] AnalyserNode configured:", {
        fftSize: analyser.fftSize,
        frequencyBinCount: analyser.frequencyBinCount,
        smoothingTimeConstant: analyser.smoothingTimeConstant,
        minDecibels: analyser.minDecibels,
        maxDecibels: analyser.maxDecibels,
      });

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      console.log("[LiveKit] Audio analyser connected and ready");

      // Monitor audio level + spectrum bands (for the equalizer UI,
      // ported from the tfcode voice-wave: 24 log-spaced bands,
      // fast attack / smooth decay).
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      // Mobile (<420px viewport): 12 bands so the voice wave fits next to
      // the mic controls in the input row. VoiceWave slices to its own count.
      const SPECTRUM_BANDS =
        typeof window !== "undefined" && window.innerWidth < 420 ? 12 : 24;
      const bands = new Array(SPECTRUM_BANDS).fill(0);
      const useful = Math.floor(dataArray.length * 0.7); // drop the near-inaudible top
      let lastLogTime = 0;
      let consecutiveSilenceCount = 0;
      let consecutiveAudioCount = 0;
      let detecting = false; // voice-detected latch with hysteresis

      audioLevelIntervalRef.current = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);

        // Calculate average level (0-255 range)
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = average / 255; // Normalize to 0-1

        // Also calculate peak level
        const peakLevel = Math.max(...dataArray) / 255;

        // Log-spaced bands with smooth decay (equalizer read)
        for (let b = 0; b < SPECTRUM_BANDS; b++) {
          const lo = Math.floor(Math.pow(b / SPECTRUM_BANDS, 2) * useful);
          const hi = Math.max(
            lo + 1,
            Math.floor(Math.pow((b + 1) / SPECTRUM_BANDS, 2) * useful)
          );
          let peak = 0;
          for (let i = lo; i < hi; i++) peak = Math.max(peak, dataArray[i]);
          const level = peak / 255;
          bands[b] = level > bands[b] ? level : bands[b] * 0.75;
        }

        // Voice detection: peak over the speech band (85–3400 Hz).
        // The full-spectrum average is diluted by near-silent high bins
        // and reacts too late; the speech-band peak lights up as soon
        // as the user starts talking.
        const speechHi = Math.min(
          dataArray.length,
          Math.ceil((3400 / (audioContext.sampleRate / 2)) * dataArray.length)
        );
        const speechLo = Math.floor(
          (85 / (audioContext.sampleRate / 2)) * dataArray.length
        );
        let speechBandPeak = 0;
        for (let i = speechLo; i < speechHi; i++) {
          if (dataArray[i] > speechBandPeak) speechBandPeak = dataArray[i];
        }
        speechBandPeak /= 255;

        const isDetected =
          speechBandPeak > (detecting ? 0.04 : 0.08); // hysteresis: easier to stay detected than to become detected
        detecting = isDetected;
        const now = Date.now();

        setState((prev) => ({
          ...prev,
          audioLevel: normalizedLevel,
          isAudioDetected: isDetected,
          audioSpectrum: [...bands],
        }));

        // Track consecutive silence/audio for state change logging
        if (isDetected) {
          consecutiveAudioCount++;
          if (consecutiveSilenceCount > 10) {
            console.log(`[LiveKit] Audio resumed after ${consecutiveSilenceCount * 100}ms of silence`);
          }
          consecutiveSilenceCount = 0;
        } else {
          consecutiveSilenceCount++;
          if (consecutiveAudioCount > 10) {
            console.log(`[LiveKit] Audio stopped after ${consecutiveAudioCount * 100}ms of audio`);
          }
          consecutiveAudioCount = 0;
        }

        // Log audio levels periodically (every 2 seconds when audio is detected)
        if (isDetected && now - lastLogTime > 2000) {
          console.log(`[LiveKit] Audio level: avg=${(normalizedLevel * 100).toFixed(1)}%, peak=${(peakLevel * 100).toFixed(1)}%`);
          lastLogTime = now;
        }
      }, 100); // Update every 100ms

      console.log("[LiveKit] Audio level monitoring started successfully");
    } catch (error) {
      console.error("[LiveKit] Failed to start audio monitoring:", error);
      console.error("[LiveKit] Error details:", {
        name: error.name,
        message: error.message,
        constraint: error.constraint,
      });

      // Provide specific error messages
      if (error.name === 'NotAllowedError') {
        console.error("[LiveKit] Microphone access denied by user or browser policy");
      } else if (error.name === 'NotFoundError') {
        console.error("[LiveKit] No microphone device found");
      } else if (error.name === 'NotReadableError') {
        console.error("[LiveKit] Microphone is in use by another application");
      } else if (error.name === 'OverconstrainedError') {
        console.error("[LiveKit] Microphone constraints could not be satisfied");
      }

      setState((prev) => ({
        ...prev,
        error: `Microphone access error: ${error.message}`,
      }));
    }
  }, []);

  /**
   * Stop monitoring audio level
   */
  const stopAudioLevelMonitoring = useCallback(() => {
    console.log("[LiveKit] Stopping audio level monitoring...");

    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log("[LiveKit] Microphone track stopped");
      });
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    setState((prev) => ({
      ...prev,
      audioLevel: 0,
      isAudioDetected: false,
    }));

    console.log("[LiveKit] Audio level monitoring stopped");
  }, []);

  /**
   * Fetch access token from backend API
   * The backend generates the JWT with proper metadata for the agent
   *
   * Live-session mode: when tokenFn is provided it takes precedence — it
   * calls /live/{id}/voice/start (tracking + mode lock) and returns the
   * same {token, url, room_name} shape as the raw token endpoint.
   */
  const fetchToken = useCallback(async () => {
    if (tokenFn) {
      console.log("[LiveKit] Fetching token via live session");
      const data = await tokenFn();
      if (!data?.token) {
        throw new Error(
          data?.errorMessage || "Live voice start returned no token"
        );
      }
      console.log(
        "[LiveKit] Received live-session token for room:",
        data.room_name,
        "url:",
        data.url
      );
      return {
        token: data.token,
        roomName: data.room_name,
        url: data.url,
      };
    }

    if (!tokenEndpoint) {
      throw new Error(
        "tokenEndpoint or tokenFn is required for LiveKit connection"
      );
    }

    console.log("[LiveKit] Fetching token from:", tokenEndpoint);

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(apiKey && { "x-api-key": apiKey }),
      },
      body: JSON.stringify({
        workspace_id: workspaceId,
        chat_id: chatId,
        agent_id: agentId,
        participant_name: participantName,
        room_name: customRoomName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to fetch token: ${response.status}`
      );
    }

    const data = await response.json();

    console.log(
      "[LiveKit] Received token for room:",
      data.room_name,
      "url:",
      data.url
    );

    return {
      token: data.token,
      roomName: data.room_name,
      url: data.url,
    };
  }, [
    tokenFn,
    tokenEndpoint,
    apiKey,
    workspaceId,
    chatId,
    agentId,
    participantName,
    customRoomName,
  ]);

  /**
   * Handle track subscription (agent audio)
   */
  const handleTrackSubscribed = useCallback(
    async (track, publication, participant) => {
      const livekit = liveKitModuleRef.current;
      if (!livekit) return;

      console.log(
        "[LiveKit] Track subscribed:",
        track.kind,
        "from:",
        participant.identity
      );

      // Only handle audio tracks from agent
      if (
        track.kind === livekit.Track.Kind.Audio &&
        participant.kind === livekit.ParticipantKind.AGENT
      ) {
        // Attach audio track to play through speakers
        const audioElement = track.attach();
        audioElement.autoplay = true;
        audioElement.volume = 1.0;
        // Mobile browsers ignore autoplay on elements without playsinline;
        // keep the element invisible but in the DOM so iOS Safari plays it.
        try { audioElement.setAttribute("playsinline", "true"); } catch (e) {}
        audioElement.style.display = 'none'; // Hide the audio element
        document.body.appendChild(audioElement);
        audioElementsRef.current.push(audioElement);

        // Ensure audio starts playing (needed for some browsers/Electron)
        try {
          await audioElement.play();
          console.log("[LiveKit] Agent audio playing");
        } catch (playErr) {
          console.warn("[LiveKit] Auto-play prevented, audio may require user interaction:", playErr.message);
        }

        console.log("[LiveKit] Agent audio attached");

        // Route audio ONLY to the user-selected output device, if any.
        // No guessing: if nothing is selected the browser default is used.
        try {
          const selectedDeviceId = selectedAudioOutputDeviceRef.current;

          if (selectedDeviceId && audioElement.setSinkId) {
            console.log("[LiveKit] Routing audio to user-selected output device:", selectedDeviceId);
            await audioElement.setSinkId(selectedDeviceId);
            console.log("[LiveKit] Audio routed to selected device successfully");
          }
        } catch (err) {
          console.warn("[LiveKit] Could not set audio output device:", err.message);
        }

        setState((prev) => ({ ...prev, isAgentSpeaking: true }));
      }
    },
    []
  );

  /**
   * Handle track unsubscription
   */
  const handleTrackUnsubscribed = useCallback(
    (track, publication, participant) => {
      const livekit = liveKitModuleRef.current;
      if (!livekit) return;

      console.log(
        "[LiveKit] Track unsubscribed:",
        track.kind,
        "from:",
        participant.identity
      );

      if (
        track.kind === livekit.Track.Kind.Audio &&
        participant.kind === livekit.ParticipantKind.AGENT
      ) {
        // Detach and remove audio elements
        track.detach().forEach((element) => {
          element.remove();
        });

        setState((prev) => ({ ...prev, isAgentSpeaking: false }));
      }
    },
    []
  );

  /**
   * Handle participant connected
   */
  const handleParticipantConnected = useCallback(
    (participant) => {
      const livekit = liveKitModuleRef.current;
      if (!livekit) return;

      console.log("[LiveKit] Participant connected:", participant.identity);

      if (participant.kind === livekit.ParticipantKind.AGENT) {
        console.log("[LiveKit] Agent connected!");

        // Listen for agent state attribute changes
        const updateAgentState = () => {
          const agentStateAttr = participant.attributes?.["lk.agent.state"];
          if (agentStateAttr) {
            setState((prev) => ({ ...prev, agentState: agentStateAttr }));
            onAgentStateChange?.(agentStateAttr);
          }
        };

        // Initial state check
        updateAgentState();

        // Listen for attribute changes
        participant.on("attributesChanged", updateAgentState);
      }
    },
    [onAgentStateChange]
  );

  /**
   * Handle participant disconnected
   */
  const handleParticipantDisconnected = useCallback((participant) => {
    const livekit = liveKitModuleRef.current;
    if (!livekit) return;

    console.log("[LiveKit] Participant disconnected:", participant.identity);

    if (participant.kind === livekit.ParticipantKind.AGENT) {
      setState((prev) => ({
        ...prev,
        agentState: AGENT_STATES.DISCONNECTED,
        isAgentSpeaking: false,
      }));
    }
  }, []);

  /**
   * Handle data received (transcripts, etc.)
   */
  const handleDataReceived = useCallback(
    (payload, participant, kind, topic) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        console.log("[LiveKit] Data received:", topic, data);

        // Handle transcripts
        if (topic === "lk.transcription" || data.type === "transcription") {
          const { text, isFinal, participantIdentity } = data;

          if (participantIdentity === participantName) {
            // User transcript
            setState((prev) => ({ ...prev, transcript: text }));
          } else {
            // Agent transcript
            setState((prev) => ({ ...prev, agentTranscript: text }));
            if (isFinal) {
              onTranscript?.(text, {
                participant: participantIdentity,
                isFinal,
              });
            }
          }
        }
      } catch (error) {
        console.warn("[LiveKit] Failed to parse data:", error);
      }
    },
    [participantName, onTranscript]
  );

  /**
   * Handle audio playback status (for browsers requiring user interaction)
   */
  const handleAudioPlaybackStatusChanged = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    if (!room.canPlaybackAudio) {
      console.warn(
        "[LiveKit] Audio playback blocked - user interaction required"
      );
      setState((prev) => ({
        ...prev,
        error: "Audio playback requires user interaction",
      }));
    }
  }, []);

  /**
   * Start audio playback (call after user interaction if blocked)
   */
  const startAudioPlayback = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    try {
      await room.startAudio();
      setState((prev) => ({ ...prev, error: null }));
      console.log("[LiveKit] Audio playback started");
    } catch (error) {
      console.error("[LiveKit] Failed to start audio playback:", error);
    }
  }, []);

  /**
   * Connect to LiveKit room
   */
  const connect = useCallback(async () => {
    if (roomRef.current) {
      console.warn("[LiveKit] Already connected");
      return;
    }

    setState((prev) => ({
      ...prev,
      connectionState: CONNECTION_STATES.CONNECTING,
      error: null,
    }));

    try {
      // Load SDK
      const livekit = await loadLiveKitSDK();

      // Fetch token from backend API
      const { token, roomName, url: tokenUrl } = await fetchToken();

      // Create room with optimal settings for voice
      const room = new livekit.Room({
        adaptiveStream: false, // Not needed for audio-only
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Setup event handlers
      room
        .on(livekit.RoomEvent.TrackSubscribed, handleTrackSubscribed)
        .on(livekit.RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
        .on(livekit.RoomEvent.ParticipantConnected, handleParticipantConnected)
        .on(
          livekit.RoomEvent.ParticipantDisconnected,
          handleParticipantDisconnected
        )
        .on(livekit.RoomEvent.DataReceived, handleDataReceived)
        .on(
          livekit.RoomEvent.AudioPlaybackStatusChanged,
          handleAudioPlaybackStatusChanged
        )
        .on(livekit.RoomEvent.Disconnected, () => {
          console.log("[LiveKit] Disconnected from room");
          setState((prev) => ({
            ...prev,
            connectionState: CONNECTION_STATES.DISCONNECTED,
            agentState: AGENT_STATES.DISCONNECTED,
          }));
        })
        .on(livekit.RoomEvent.Reconnecting, () => {
          console.log("[LiveKit] Reconnecting...");
          setState((prev) => ({
            ...prev,
            connectionState: CONNECTION_STATES.RECONNECTING,
          }));
        })
        .on(livekit.RoomEvent.Reconnected, () => {
          console.log("[LiveKit] Reconnected");
          setState((prev) => ({
            ...prev,
            connectionState: CONNECTION_STATES.CONNECTED,
          }));
        });

      // Connect to room (prefer URL from token response, then serverUrl prop, then env config)
      const connectUrl =
        tokenUrl || serverUrl || envConfig.REACT_APP_LIVEKIT_URL;
      console.log("[LiveKit] Connecting to:", connectUrl);
      await room.connect(connectUrl, token);
      roomRef.current = room;
      reconnectAttemptsRef.current = 0;

      console.log("[LiveKit] Connected to room:", roomName);

      setState((prev) => ({
        ...prev,
        connectionState: CONNECTION_STATES.CONNECTED,
        agentState: AGENT_STATES.INITIALIZING,
        roomName,
      }));

      // Check for existing agent participant
      room.remoteParticipants.forEach((participant) => {
        if (participant.kind === livekit.ParticipantKind.AGENT) {
          handleParticipantConnected(participant);
        }
      });
    } catch (error) {
      console.error("[LiveKit] Connection failed:", error);

      // Live-session mode: the backend voice lock was acquired by
      // tokenFn (voice/start) but we never connected — release it so the
      // user can retry or send text instead of hitting 409 forever.
      if (stopFn) {
        Promise.resolve()
          .then(stopFn)
          .catch((e) =>
            console.log(
              "[LiveKit] voice stop on connect-failure (ignored):",
              e?.message
            )
          );
      }

      setState((prev) => ({
        ...prev,
        connectionState: CONNECTION_STATES.ERROR,
        error: error.message,
      }));

      onError?.(error);
    }
  }, [
    serverUrl,
    loadLiveKitSDK,
    fetchToken,
    stopFn,
    handleTrackSubscribed,
    handleTrackUnsubscribed,
    handleParticipantConnected,
    handleParticipantDisconnected,
    handleDataReceived,
    handleAudioPlaybackStatusChanged,
    onError,
  ]);

  /**
   * Disconnect from LiveKit room
   */
  const disconnect = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    console.log("[LiveKit] Disconnecting...");

    // Stop audio level monitoring
    stopAudioLevelMonitoring();

    // Clean up audio elements
    audioElementsRef.current.forEach((element) => {
      element.pause();
      element.remove();
    });
    audioElementsRef.current = [];

    // Disconnect room
    room.disconnect();
    roomRef.current = null;

    // Live-session mode: release the backend voice mode lock so text
    // turns can resume on the same session. Fire-and-forget — the room
    // is already down and the backend has its own session cleanup.
    if (stopFn) {
      Promise.resolve()
        .then(stopFn)
        .catch((e) =>
          console.log("[LiveKit] voice stop error (ignored):", e?.message)
        );
    }

    setState((prev) => ({
      ...prev,
      connectionState: CONNECTION_STATES.DISCONNECTED,
      agentState: AGENT_STATES.DISCONNECTED,
      isMicrophoneEnabled: false,
      isAgentSpeaking: false,
      transcript: "",
      agentTranscript: "",
      roomName: null,
      audioLevel: 0,
      isAudioDetected: false,
      audioSpectrum: [],
    }));

    console.log("[LiveKit] Disconnected");
  }, [stopAudioLevelMonitoring, stopFn]);

  /**
   * Enable/disable microphone
   * @param {boolean} enabled - Whether to enable or disable the microphone
   * @param {string} [preferredDeviceId] - Optional device ID to use (skips auto-detection of external devices)
   */
  const setMicrophoneEnabled = useCallback(async (enabled, preferredDeviceId = null) => {
    const room = roomRef.current;
    if (!room) {
      console.warn("[LiveKit] Not connected - cannot toggle microphone");
      return;
    }

    try {
      console.log(`[LiveKit] ${enabled ? "Enabling" : "Disabling"} microphone...`);
      if (preferredDeviceId) {
        console.log(`[LiveKit] Using preferred device: ${preferredDeviceId}`);
      }

      // Check Electron microphone permissions first
      if (enabled && isElectron()) {
        console.log("[LiveKit] Running in Electron - checking microphone permissions...");
        try {
          const status = await window.electronAPI.getMicrophonePermissionStatus();
          console.log("[LiveKit] Electron microphone permission status:", status);

          if (status !== "granted") {
            console.log("[LiveKit] Requesting microphone permission from Electron...");
            const result = await window.electronAPI.requestMicrophonePermission();
            console.log("[LiveKit] Microphone permission result:", result);

            if (!result.granted) {
              const errorMsg = result.message ||
                "Microphone access denied. Please enable it in System Preferences > Security & Privacy > Privacy > Microphone";
              console.error("[LiveKit] Microphone permission denied:", errorMsg);
              setState((prev) => ({
                ...prev,
                error: errorMsg,
              }));

              // Offer to open system preferences
              if (result.status === "denied" || result.status === "restricted") {
                console.log("[LiveKit] Opening system preferences for microphone...");
                await window.electronAPI.openSystemPreferencesMicrophone();
              }
              return;
            }
          }
        } catch (electronErr) {
          console.error("[LiveKit] Electron permission check failed:", electronErr);
          // Continue anyway - the permission might still work
        }
      }

      // Use user-selected device if provided, otherwise log available devices
      let deviceToUse = preferredDeviceId;
      if (enabled) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = devices.filter(d => d.kind === 'audioinput');
          console.log("[LiveKit] Available audio input devices:", audioInputs.length);
          audioInputs.forEach((device, i) => {
            console.log(`[LiveKit]   Device ${i + 1}: ${device.label || 'Unknown'} (${device.deviceId.slice(0, 8)}...)`);
          });

          // Check microphone permission state
          if (navigator.permissions) {
            try {
              const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
              console.log("[LiveKit] Microphone permission state:", permissionStatus.state);
            } catch (permErr) {
              console.log("[LiveKit] Could not query microphone permission:", permErr.message);
            }
          }
        } catch (enumErr) {
          console.warn("[LiveKit] Could not enumerate devices:", enumErr.message);
        }
      }

      await room.localParticipant.setMicrophoneEnabled(enabled, {
        ...(deviceToUse && { deviceId: { exact: deviceToUse } }),
      });

      // Switch to user-selected device if provided and not already used
      // during capture
      if (enabled && deviceToUse) {
        try {
          console.log("[LiveKit] Switching to selected audio input device:", deviceToUse.slice(0, 8) + '...');
          await room.switchActiveDevice('audioinput', deviceToUse);
          console.log("[LiveKit] Successfully switched to selected audio input");
        } catch (switchErr) {
          console.warn("[LiveKit] Failed to switch to selected audio input:", switchErr.message);
        }
      }

      // Log microphone track details after enabling
      if (enabled) {
        const micTrack = room.localParticipant.getTrackPublication('microphone');
        if (micTrack) {
          console.log("[LiveKit] Microphone track created:", {
            trackSid: micTrack.trackSid,
            isMuted: micTrack.isMuted,
            isEnabled: micTrack.isEnabled,
            source: micTrack.source,
          });

          if (micTrack.track) {
            const settings = micTrack.track.mediaStreamTrack?.getSettings?.();
            if (settings) {
              console.log("[LiveKit] Microphone track settings:", {
                deviceId: settings.deviceId?.slice(0, 8) + '...',
                channelCount: settings.channelCount,
                sampleRate: settings.sampleRate,
                echoCancellation: settings.echoCancellation,
                noiseSuppression: settings.noiseSuppression,
                autoGainControl: settings.autoGainControl,
              });
            }
          }
        } else {
          console.warn("[LiveKit] Microphone enabled but no track publication found");
        }
      }

      setState((prev) => ({
        ...prev,
        isMicrophoneEnabled: enabled,
      }));

      // Start/stop audio level monitoring (same device as the published mic)
      if (enabled) {
        console.log("[LiveKit] Microphone enabled - starting audio level monitoring");
        await startAudioLevelMonitoring(deviceToUse);
      } else {
        console.log("[LiveKit] Microphone disabled - stopping audio level monitoring");
        stopAudioLevelMonitoring();
      }

      console.log("[LiveKit] Microphone", enabled ? "enabled" : "disabled", "successfully");
    } catch (error) {
      console.error("[LiveKit] Failed to toggle microphone:", error);
      console.error("[LiveKit] Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 3).join('\n'),
      });
      setState((prev) => ({
        ...prev,
        error: `Microphone error: ${error.message}`,
      }));
    }
  }, [startAudioLevelMonitoring, stopAudioLevelMonitoring]);

  /**
   * Toggle microphone
   */
  const toggleMicrophone = useCallback(async () => {
    await setMicrophoneEnabled(!state.isMicrophoneEnabled);
  }, [state.isMicrophoneEnabled, setMicrophoneEnabled]);

  /**
   * Send data to agent via RPC
   */
  const sendToAgent = useCallback(async (method, payload) => {
    const room = roomRef.current;
    const livekit = liveKitModuleRef.current;
    if (!room || !livekit) {
      console.warn("[LiveKit] Not connected - cannot send to agent");
      return null;
    }

    // Find agent participant
    let agentIdentity = null;
    room.remoteParticipants.forEach((participant) => {
      if (participant.kind === livekit.ParticipantKind.AGENT) {
        agentIdentity = participant.identity;
      }
    });

    if (!agentIdentity) {
      console.warn("[LiveKit] No agent found in room");
      return null;
    }

    try {
      const result = await room.localParticipant.performRpc({
        destinationIdentity: agentIdentity,
        method,
        payload: JSON.stringify(payload),
      });

      return JSON.parse(result);
    } catch (error) {
      console.error("[LiveKit] RPC call failed:", error);
      throw error;
    }
  }, []);

  /**
   * Register RPC method handler (for agent to call client)
   */
  const registerRpcMethod = useCallback((method, handler) => {
    const room = roomRef.current;
    if (!room) {
      console.warn("[LiveKit] Not connected - cannot register RPC method");
      return;
    }

    room.localParticipant.registerRpcMethod(method, async (data) => {
      const payload = JSON.parse(data.payload);
      const result = await handler(payload, data.callerIdentity);
      return JSON.stringify(result);
    });

    console.log("[LiveKit] Registered RPC method:", method);
  }, []);

  /**
   * Get list of available audio devices
   */
  const getAudioDevices = useCallback(async () => {
    const livekit = await loadLiveKitSDK();
    return livekit.Room.getLocalDevices("audioinput");
  }, [loadLiveKitSDK]);

  /**
   * Switch audio input device
   */
  const switchAudioDevice = useCallback(async (deviceId) => {
    const room = roomRef.current;
    if (!room) {
      console.warn("[LiveKit] Not connected - cannot switch device");
      return;
    }

    await room.switchActiveDevice("audioinput", deviceId);
    console.log("[LiveKit] Switched to audio input device:", deviceId);
  }, []);

  /**
   * Get all available audio devices (input and output)
   */
  const getAllAudioDevices = useCallback(async () => {
    try {
      // Ensure we have permission to see device labels
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      return {
        inputs: devices.filter(d => d.kind === 'audioinput').map(d => ({
          deviceId: d.deviceId,
          label: d.label,
          kind: d.kind,
        })),
        outputs: devices.filter(d => d.kind === 'audiooutput').map(d => ({
          deviceId: d.deviceId,
          label: d.label,
          kind: d.kind,
        })),
      };
    } catch (err) {
      console.error("[LiveKit] Failed to get audio devices:", err);
      return { inputs: [], outputs: [] };
    }
  }, []);

  /**
   * Switch audio output device (for agent audio playback)
   */
  const switchAudioOutputDevice = useCallback(async (deviceId) => {
    try {
      // Store the selected device ID for future audio elements
      selectedAudioOutputDeviceRef.current = deviceId;
      
      // Update all existing audio elements
      for (const audioElement of audioElementsRef.current) {
        if (audioElement.setSinkId) {
          await audioElement.setSinkId(deviceId);
        }
      }
      console.log("[LiveKit] Switched audio output to:", deviceId);
    } catch (err) {
      console.error("[LiveKit] Failed to switch audio output:", err);
    }
  }, []);

  // Auto-connect if enabled
  useEffect(() => {
    if (autoConnect && serverUrl && (tokenEndpoint || tokenFn) && workspaceId && apiKey) {
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, serverUrl, tokenEndpoint, tokenFn, workspaceId, apiKey]);

  // Cleanup on unmount — call the LATEST disconnect (a plain effect
  // closure would capture the first render's disconnect, whose stopFn
  // may still be undefined while live-session adoption is in flight,
  // leaking the backend voice lock).
  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;
  useEffect(() => {
    return () => {
      disconnectRef.current();
    };
  }, []);

  /**
   * Publish a data message to the room (e.g. attachment_added) so the
   * agent worker learns about mid-session events in real time.
   */
  const sendData = useCallback(async (payload) => {
    const room = roomRef.current;
    if (!room) {
      console.warn("[LiveKit] Not connected - cannot send data message");
      return false;
    }
    try {
      const encoder = new TextEncoder();
      await room.localParticipant.publishData(encoder.encode(JSON.stringify(payload)), {
        reliable: true,
      });
      console.log("[LiveKit] Data message published:", payload?.type);
      return true;
    } catch (err) {
      console.error("[LiveKit] Failed to publish data message:", err);
      return false;
    }
  }, []);

  return {
    // State
    isConnected: state.connectionState === CONNECTION_STATES.CONNECTED,
    connectionState: state.connectionState,
    agentState: state.agentState,
    isMicrophoneEnabled: state.isMicrophoneEnabled,
    isAgentSpeaking: state.isAgentSpeaking,
    transcript: state.transcript,
    agentTranscript: state.agentTranscript,
    error: state.error,
    roomName: state.roomName,
    audioLevel: state.audioLevel,
    isAudioDetected: state.isAudioDetected,
    audioSpectrum: state.audioSpectrum,

    // Connection methods
    connect,
    disconnect,

    // Microphone control
    setMicrophoneEnabled,
    toggleMicrophone,
    checkMicrophonePermission,
    requestMicrophonePermission,

    // Audio playback (for browsers that block autoplay)
    startAudioPlayback,

    // Agent communication
    sendToAgent,
    registerRpcMethod,
    sendData,

    // Device management
    getAudioDevices,
    switchAudioDevice,
    getAllAudioDevices,
    switchAudioOutputDevice,

    // Constants
    AGENT_STATES,
    CONNECTION_STATES,
  };
};

export default useLiveKitVoice;
